/*
  Kijestic Skin-AI - Phase 1 (on-device, $0, no dependencies, no upload).
  Computes REAL, explainable skin metrics from captured pixels via canvas:
    Radiance  (skin brightness / glow)
    Smoothness (inverse of high-frequency texture energy)
    Evenness  (uniformity of skin tone, low luma variance)
    Clarity   (few dark spots relative to local skin tone)
  Skin pixels are isolated with a YCbCr+RGB skin-tone mask so hair/background
  don't pollute the read. Averages across the multi-angle shots for robustness.

  This is the first consumer of the reusable Kijestic Skin-AI engine. Output
  schema is intentionally stable so other med spas and our photo apps can reuse it:
    { ok, tone, metrics:[{key,label,score}], topConcern:{label,treatment} }

  NOTE: guidance, not a medical diagnosis. Phase 2 adds trained models server-side.
*/
window.KijesticSkinAI = (function () {
  'use strict';

  function loadImg(src) {
    return new Promise(function (res, rej) {
      var i = new Image();
      i.onload = function () { res(i); };
      i.onerror = rej;
      i.src = src;
    });
  }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  // Linear map to 1..99 (lo may be greater than hi to invert).
  function map(v, lo, hi) { return clamp(Math.round((v - lo) / (hi - lo) * 100), 1, 99); }

  // Mean |neighbour difference| over pixel pairs where BOTH pixels are in the skin mask.
  //
  // This used to compare every mask pixel with its right and lower neighbour whatever those
  // neighbours were, so the step from cheek to hair, to a spectacle frame or to the wall
  // counted as "texture" - the biggest differences in the frame, at the mask's edge, on
  // skin that has none. Pairs that leave the mask are now skipped, so the score describes
  // skin against skin. Cross-mask edges were the flaw found reviewing INT-3's copy.
  function textureEnergy(lum, skin, isSkin, W, H) {
    var grad = 0, gc = 0;
    for (var k = 0; k < skin.length; k++) {
      var p = skin[k], x = p % W, y = (p - x) / W;
      if (x < W - 1 && isSkin[p + 1]) { grad += Math.abs(lum[p] - lum[p + 1]); gc++; }
      if (y < H - 1 && isSkin[p + W]) { grad += Math.abs(lum[p] - lum[p + W]); gc++; }
    }
    return gc ? grad / gc : 0;
  }

  function analyzeOne(img) {
    var W = 256, H = Math.max(1, Math.round((img.height / img.width) * 256));
    var c = document.createElement('canvas'); c.width = W; c.height = H;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);
    var d;
    try { d = ctx.getImageData(0, 0, W, H).data; } catch (e) { return null; }

    var total = W * H;
    var lum = new Float32Array(total);
    var skin = [];
    for (var i = 0, p = 0; i < d.length; i += 4, p++) {
      var r = d[i], g = d[i + 1], b = d[i + 2];
      var y = 0.299 * r + 0.587 * g + 0.114 * b; lum[p] = y;
      var cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
      var cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;
      // skin-tone gate, widened to cover the full Fitzpatrick range + varied lighting
      if (r > 50 && g > 25 && b > 12 && cr >= 128 && cr <= 188 && cb >= 70 && cb <= 138) {
        skin.push(p);
      }
    }
    // Coverage fallback: if the gate finds too little skin (tricky lighting/tone),
    // analyze the central face region instead of giving up.
    if (skin.length < total * 0.05) {
      skin = [];
      var x0 = Math.floor(W * 0.22), x1 = Math.floor(W * 0.78), y0 = Math.floor(H * 0.15), y1 = Math.floor(H * 0.90);
      for (var yy = y0; yy < y1; yy++) for (var xx = x0; xx < x1; xx++) skin.push(yy * W + xx);
      if (!skin.length) return { ok: false };
    }

    var n = skin.length, sumL = 0;
    for (var k = 0; k < n; k++) sumL += lum[skin[k]];
    var mL = sumL / n;
    if (mL < 1) return { ok: false };

    var varL = 0;
    for (var k2 = 0; k2 < n; k2++) { var dd = lum[skin[k2]] - mL; varL += dd * dd; }
    var sdL = Math.sqrt(varL / n);

    var isSkin = new Uint8Array(total);
    for (var m = 0; m < n; m++) isSkin[skin[m]] = 1;
    var texture = textureEnergy(lum, skin, isSkin, W, H);

    // Tone-invariant features (verified stable across brightness + skin tone):
    var glow = 0, glowTh = mL + sdL, spots = 0, spotTh = 0.62 * mL;
    for (var k4 = 0; k4 < n; k4++) { var lv = lum[skin[k4]]; if (lv > glowTh) glow++; if (lv < spotTh) spots++; }

    return {
      ok: true,
      glow: glow / n,      // relative highlight ratio -> radiance/luminosity, tone-invariant
      cvL: sdL / mL,       // coefficient of variation -> tone evenness, tone-invariant
      texN: texture / mL,  // brightness-normalized texture energy -> smoothness
      spot: spots / n,     // pixels below 72% of mean -> dark spots/shadows (proportional)
      tone: mL > 175 ? 'fair' : (mL > 120 ? 'medium' : 'deep')
    };
  }

  // Tone-invariant ranges, calibrated on a diverse 15-face set (varied skin tones,
  // lighting, age, gender) + brightness variants. glow 0.06-0.23, cvL 0.27-0.53,
  // texN 0.03-0.09, spot 0.11-0.32 (threshold 0.62*mean).
  function scoreRaw(raw) {
    return {
      radiance: map(raw.glow, 0.05, 0.30),
      smoothness: map(raw.texN, 0.075, 0.030),
      evenness: map(raw.cvL, 0.46, 0.25),
      clarity: map(raw.spot, 0.25, 0.10),
      tone: raw.tone
    };
  }

  var CONCERN = {
    radiance: { label: 'dullness', treatment: 'HydraFacial' },
    smoothness: { label: 'uneven texture', treatment: 'Microneedling' },
    evenness: { label: 'uneven tone', treatment: 'Laser Genesis' },
    clarity: { label: 'dark spots', treatment: 'Carbon laser peel' }
  };

  // ---- Regional problem-area map (on-device "here's where") -------------------
  // Default face zones in normalized [0..1] coords for a centered front-facing shot.
  // The coach can pass precise zones derived from MediaPipe landmarks instead.
  var DEFAULT_ZONES = [
    { key: 'forehead', label: 'Forehead', box: [0.30, 0.12, 0.40, 0.15] },
    { key: 'tzone', label: 'T-zone', box: [0.42, 0.30, 0.16, 0.30] },
    { key: 'cheekL', label: 'Left cheek', box: [0.17, 0.42, 0.22, 0.24] },
    { key: 'cheekR', label: 'Right cheek', box: [0.61, 0.42, 0.22, 0.24] },
    { key: 'undereye', label: 'Under-eye', box: [0.30, 0.34, 0.40, 0.07] },
    { key: 'chin', label: 'Chin & jaw', box: [0.34, 0.72, 0.32, 0.16] }
  ];

  // Composite 1..99 "skin health" score for a sub-rectangle of the image, using the
  // same tone-invariant primitives as the full-face read. Skin-masked within the box,
  // falling back to all box pixels under tricky lighting.
  function scoreRegion(d, W, H, box) {
    var x0 = Math.max(0, Math.floor(box[0] * W)), y0 = Math.max(0, Math.floor(box[1] * H));
    var x1 = Math.min(W, Math.ceil((box[0] + box[2]) * W)), y1 = Math.min(H, Math.ceil((box[1] + box[3]) * H));
    if (x1 - x0 < 3 || y1 - y0 < 3) return null;
    var lum = [], idx = [];
    for (var y = y0; y < y1; y++) {
      for (var x = x0; x < x1; x++) {
        var p = (y * W + x) * 4, r = d[p], g = d[p + 1], b = d[p + 2];
        var yv = 0.299 * r + 0.587 * g + 0.114 * b;
        var cb = 128 - 0.168736 * r - 0.331264 * g + 0.5 * b;
        var cr = 128 + 0.5 * r - 0.418688 * g - 0.081312 * b;
        if (r > 50 && g > 25 && b > 12 && cr >= 128 && cr <= 188 && cb >= 70 && cb <= 138) { lum.push(yv); idx.push([x, y]); }
      }
    }
    // Fallback: too little skin in the box -> use every pixel in it.
    if (lum.length < (x1 - x0) * (y1 - y0) * 0.12) {
      lum = []; idx = [];
      for (var yy = y0; yy < y1; yy++) for (var xx = x0; xx < x1; xx++) {
        var pp = (yy * W + xx) * 4; lum.push(0.299 * d[pp] + 0.587 * d[pp + 1] + 0.114 * d[pp + 2]); idx.push([xx, yy]);
      }
    }
    var n = lum.length; if (!n) return null;
    var sum = 0; for (var i = 0; i < n; i++) sum += lum[i];
    var mL = sum / n; if (mL < 1) return null;
    var varL = 0; for (var i2 = 0; i2 < n; i2++) { var dd = lum[i2] - mL; varL += dd * dd; }
    var sdL = Math.sqrt(varL / n);
    // texture: mean |neighbour diff| over pairs where BOTH pixels are gated - the same rule
    // textureEnergy() applies to the whole-face metric, by position rather than by list order.
    //
    // Walking consecutive idx entries and requiring x + 1 looks equivalent but is not: it can
    // only ever find horizontal pairs, so a zone the gate happens to fragment (alternate
    // columns, a brow or stubble breaking up every row) yields NO pairs at all. Texture then
    // reads as 0, smoothness maps to 99, and a genuinely textured zone scores as near-flawless
    // and is never picked as the customer's focus zone. Measured on a 40x40 half-gated zone:
    // 91 fragmented vs 72 solid for identical underlying texture. Pairing by position finds
    // the vertical neighbours too, so the fragmented and solid cases agree.
    var at = new Map();
    for (var i5 = 0; i5 < n; i5++) at.set(idx[i5][1] * W + idx[i5][0], i5);
    var grad = 0, gc = 0;
    for (var i3 = 0; i3 < n; i3++) {
      var gx = idx[i3][0], gy = idx[i3][1];
      // The x + 1 < x1 / y + 1 < y1 bounds are load-bearing, not defensive. Without the x
      // bound, a pixel in the last column keys gy * W + W, which is the FIRST pixel of the
      // NEXT row - a pair straight across the image that is not a neighbour at all. On a
      // 40x40 checkerboard that alone manufactured 19 pairs out of nothing.
      var right = gx + 1 < x1 ? at.get(gy * W + gx + 1) : undefined;
      if (right !== undefined) { grad += Math.abs(lum[right] - lum[i3]); gc++; }
      var below = gy + 1 < y1 ? at.get((gy + 1) * W + gx) : undefined;
      if (below !== undefined) { grad += Math.abs(lum[below] - lum[i3]); gc++; }
    }
    // No in-mask pair anywhere means texture was not measured, which is not the same as no
    // texture. Scoring it 0 would hand the zone a 99 for smoothness. Drop the zone instead:
    // analyzeRegions() already filters null scores out.
    if (!gc) return null;
    var texN = (grad / gc) / mL;
    var glow = 0, glowTh = mL + sdL, spots = 0, spotTh = 0.62 * mL;
    for (var i4 = 0; i4 < n; i4++) { if (lum[i4] > glowTh) glow++; if (lum[i4] < spotTh) spots++; }
    var radiance = map(glow / n, 0.05, 0.30);
    var smoothness = map(texN, 0.075, 0.030);
    var evenness = map(sdL / mL, 0.46, 0.25);
    var clarity = map(spots / n, 0.25, 0.10);
    return Math.round((radiance + smoothness + evenness + clarity) / 4);
  }

  // Public: score each face zone of ONE image. zones optional (defaults to a centered face).
  function analyzeRegions(dataUrl, zones) {
    zones = zones && zones.length ? zones : DEFAULT_ZONES;
    return loadImg(dataUrl).then(function (img) {
      var W = 320, H = Math.max(1, Math.round((img.height / img.width) * 320));
      var c = document.createElement('canvas'); c.width = W; c.height = H;
      var ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, W, H);
      var d;
      try { d = ctx.getImageData(0, 0, W, H).data; } catch (e) { return { ok: false }; }
      var out = zones.map(function (z) {
        return { key: z.key, label: z.label, box: z.box, score: scoreRegion(d, W, H, z.box) };
      }).filter(function (r) { return r.score != null; });
      if (!out.length) return { ok: false };
      // Band each zone; mark the 1-2 lowest as the focus areas.
      var sorted = out.slice().sort(function (a, b) { return a.score - b.score; });
      var nFocus = (sorted[1] && sorted[1].score - sorted[0].score < 14) ? 2 : 1;
      var focusKeys = {}; sorted.slice(0, nFocus).forEach(function (r) { focusKeys[r.key] = true; });
      out.forEach(function (r) {
        r.band = focusKeys[r.key] ? 'focus' : (r.score >= 70 ? 'strong' : (r.score >= 45 ? 'good' : 'fair'));
      });
      return { ok: true, zones: out };
    }).catch(function () { return { ok: false }; });
  }

  function analyze(dataUrls) {
    return Promise.all(dataUrls.map(loadImg)).then(function (imgs) {
      var per = imgs.map(function (im) {
        var raw = analyzeOne(im);
        return raw && raw.ok ? scoreRaw(raw) : null;
      }).filter(Boolean);
      if (!per.length) return { ok: false };

      var keys = ['radiance', 'smoothness', 'evenness', 'clarity'];
      var avg = {};
      keys.forEach(function (k) {
        avg[k] = Math.round(per.reduce(function (s, p) { return s + p[k]; }, 0) / per.length);
      });
      var metrics = [
        { key: 'radiance', label: 'Radiance', score: avg.radiance },
        { key: 'smoothness', label: 'Smoothness', score: avg.smoothness },
        { key: 'evenness', label: 'Evenness', score: avg.evenness },
        { key: 'clarity', label: 'Clarity', score: avg.clarity }
      ];
      var lowest = metrics.slice().sort(function (a, b) { return a.score - b.score; })[0];
      return { ok: true, tone: per[0].tone, metrics: metrics, topConcern: CONCERN[lowest.key], angles: per.length };
    }).catch(function () { return { ok: false }; });
  }

  return { analyze: analyze, analyzeRegions: analyzeRegions, version: '1.2.0-phase1' };
})();
